import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, Gauge } from "lucide-react";
import { ThemeToggle } from "@/components/site/ThemeToggle";

const NAV = [
  { href: "#inicio", label: "Inicio" },
  { href: "#sgm", label: "SGM" },
  { href: "#servicios", label: "Servicios" },
  { href: "#sectores", label: "Sectores" },
  { href: "#contacto", label: "Contacto" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goPortal = () => {
    if (!user) navigate("/login");
    else if (user.role === "admin") navigate("/admin");
    else navigate("/portal");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#1A1A1F]/85 border-b border-[#2E2E33]" : "bg-transparent theme-fixed-dark"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <div className="w-10 h-10 border border-[#F59E0B] flex items-center justify-center bg-[#F59E0B]/10">
            <Gauge className="w-5 h-5 text-[#F59E0B]" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-black text-sm tracking-tight uppercase text-white">SGM México</div>
            <div className="font-mono text-[10px] text-[#A1A1AA] tracking-widest uppercase">Cumplimiento Normativo</div>
          </div>
        </Link>

        {onLanding && (
          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="nav-link" data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </a>
            ))}
          </nav>
        )}

        <div className="hidden lg:flex items-center gap-3">
          <ThemeToggle testId="header-theme-toggle" />
          {user ? (
            <>
              <button onClick={goPortal} className="btn-ghost" data-testid="header-portal-btn">
                {user.role === "admin" ? "Panel Admin" : "Mi Portal"}
              </button>
              <button onClick={async () => { await logout(); navigate("/"); }} className="btn-primary" data-testid="header-logout-btn">
                Salir
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="header-login-link">Acceso Clientes</Link>
              <a href="#contacto" className="btn-primary" data-testid="header-cta-btn">Cotizar Servicio</a>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <ThemeToggle testId="header-theme-toggle-mobile" />
          <button
            className="text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#1A1A1F] border-t border-[#2E2E33] px-6 py-6 space-y-4" data-testid="mobile-menu">
          {onLanding && NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block nav-link">
              {n.label}
            </a>
          ))}
          <div className="pt-4 flex flex-col gap-3">
            {user ? (
              <>
                <button onClick={goPortal} className="btn-ghost w-full">{user.role === "admin" ? "Panel Admin" : "Mi Portal"}</button>
                <button onClick={async () => { await logout(); navigate("/"); setOpen(false); }} className="btn-primary w-full">Salir</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost w-full text-center">Acceso Clientes</Link>
                <a href="#contacto" onClick={() => setOpen(false)} className="btn-primary w-full">Cotizar Servicio</a>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
